import { SugarModel } from "../loader/SugarModel";
import { SugarModelJoint } from "../loader/SugarModelJoint";
import { logger } from "./Logger";

type JointSlotDump = {
  jointId: number | string;
  inType: string;
  outType: string;
  binded: boolean;
  freeze: boolean;
  partner?: { modelUuid: string; modelId?: number; jointId: number | string };
};

type ModelSlotDump = {
  kind: "configurator" | "model";
  uuid: string;
  id?: number;
  name?: string;
  childCount?: number;
  joints: JointSlotDump[];
  children?: ModelSlotDump[];
};

/** Duck-type CFG root — avoids importing SugarModelConfigurator (circular with loader). */
type ConfiguratorLike = SugarModel & { models: readonly SugarModel[] };

function isConfiguratorLike(model: SugarModel): model is ConfiguratorLike {
  return Array.isArray((model as ConfiguratorLike).models);
}

function dumpJoint(joint: SugarModelJoint): JointSlotDump {
  const partner = joint.bindedJoint;
  return {
    jointId: joint.jointId,
    inType: joint.inType,
    outType: joint.outType,
    binded: joint.binded,
    freeze: !!joint.freeze,
    partner: partner
      ? {
          modelUuid: partner.model?.uuid ?? "?",
          modelId: partner.model?.userData?.info?.id,
          jointId: partner.jointId,
        }
      : undefined,
  };
}

function dumpModel(model: SugarModel): ModelSlotDump {
  if (isConfiguratorLike(model)) {
    return {
      kind: "configurator",
      uuid: model.uuid,
      name: model.name,
      childCount: model.models.length,
      joints: [],
      children: model.models.map(dumpModel),
    };
  }
  return {
    kind: "model",
    uuid: model.uuid,
    id: model.userData?.info?.id,
    name: model.name,
    joints: (model.joints ?? []).map(dumpJoint),
  };
}

function flattenForTable(
  model: SugarModel,
  configuratorUuid?: string,
): Array<{
  configurator: string;
  modelUuid: string;
  modelId: string | number;
  jointId: string | number;
  inType: string;
  outType: string;
  binded: boolean;
  freeze: boolean;
  partner: string;
}> {
  if (isConfiguratorLike(model)) {
    return model.models.flatMap((child) =>
      flattenForTable(child, model.uuid),
    );
  }
  const cfg = configuratorUuid ?? model.configurator?.uuid ?? "(root)";
  const joints = model.joints ?? [];
  if (joints.length === 0) {
    return [
      {
        configurator: String(cfg).slice(0, 8),
        modelUuid: model.uuid.slice(0, 8),
        modelId: model.userData?.info?.id ?? "?",
        jointId: "-",
        inType: "-",
        outType: "-",
        binded: false,
        freeze: false,
        partner: "-",
      },
    ];
  }
  return joints.map((joint) => ({
    configurator: String(cfg).slice(0, 8),
    modelUuid: model.uuid.slice(0, 8),
    modelId: model.userData?.info?.id ?? "?",
    jointId: joint.jointId,
    inType: joint.inType,
    outType: joint.outType,
    binded: joint.binded,
    freeze: !!joint.freeze,
    partner: joint.bindedJoint
      ? `${joint.bindedJoint.model?.uuid?.slice(0, 8) ?? "?"}:${joint.bindedJoint.jointId}`
      : "-",
  }));
}

/**
 * Debug dump of scene configurators + joint/slot occupancy after CFG actions.
 * Always-on for now (temporary); filter DevTools by `[CFG slots]`.
 */
export function logConfiguratorSlotState(
  reason: string,
  roots: readonly SugarModel[],
): void {
  const snapshot = {
    reason,
    rootCount: roots.length,
    roots: roots.map(dumpModel),
  };
  const label = `[CFG slots] ${reason}`;
  try {
    console.groupCollapsed(label);
    console.table(roots.flatMap((root) => flattenForTable(root)));
    console.log(snapshot);
    console.groupEnd();
  } catch {
    logger.info(label, snapshot);
  }
}
